// Tutorial win conditions for each tutorial
// Each tutorial has two win conditions: regular and secret (more challenging)

import { Position, IPiece } from '../../services/zodiacEngine/types';
import { COMMON_RACE_PATTERNS, createDualDirectionRace, createRaceToTop } from './UniversalRaceComponents';

// Diagnostic flag for the Bear-5 spurious-secret-victory investigation.
// Set true by checkTutorialWinCondition only while Bear/4 is being evaluated;
// telemetry lines are gated on this so other tutorials don't get spammed.
let bear5DiagActive = false;
function bear5Log(line: string): void {
  if (!bear5DiagActive) return;
  // console.warn so it survives prod minification + reaches browser logs.
  // Prefix is greppable in user-session captures.
  // eslint-disable-next-line no-console
  console.warn(`[BEAR5-DIAG] ${line}`);
}

// Mirror of bear5DiagActive for the Bear-4 spurious-secret-victory follow-up.
// User reported a `capture_target` secret firing on Bear 4 ("Race defence")
// without the dark crane king actually being captured — same shape as the
// Bear-5 issue. Gated to Bear/3 evaluation only so other tutorials stay quiet.
let bear4DiagActive = false;
function bear4Log(line: string): void {
  if (!bear4DiagActive) return;
  // eslint-disable-next-line no-console
  console.warn(`[BEAR4-DIAG] ${line}`);
}

// Generic win condition types for reusability
export interface WinConditionConfig {
  // Win conditions
  secretWin?: {
    type: 'reach_row' | 'capture_target' | 'turn_limit' | 'multiple' | 'capture_all_enemies' | 'reach_row_with_all_alive' | 'reach_row_with_both_pieces' | 'reach_row_with_both_pieces_anywhere' | 'capture_all_enemies_with_survivors';
    target?: string; // piece type like 'Bear', 'Crane', etc.
    secondTarget?: string; // second piece type for reach_row_with_both_pieces
    targetSide?: 'light' | 'dark';
    row?: number;
    turnLimit?: number;
    expectedCount?: number; // for reach_row_with_all_alive condition
    survivorTarget?: string; // piece type that must survive
    survivorSide?: 'light' | 'dark'; // side of survivor pieces
    minSurvivors?: number; // minimum number of survivors required
    minTurnCount?: number; // Top-level floor: secretWin cannot fire below this turn count.
    requireKing?: boolean; // For capture_target/reach_row: only count the king.
    conditions?: Array<{
      type: 'reach_row' | 'capture_target' | 'turn_limit' | 'capture_all_enemies_with_survivors' | 'all_of' | 'pieces_alive';
      target?: string;
      targetSide?: 'light' | 'dark';
      row?: number;
      turnLimit?: number;
      survivorTarget?: string;
      survivorSide?: 'light' | 'dark';
      minSurvivors?: number;
      requireKing?: boolean; // For capture_target: only count the king's presence
      minCount?: number; // For pieces_alive
      mustBeKing?: boolean; // For pieces_alive: count only kings
      mustNotBeKing?: boolean; // For pieces_alive: count only non-king pieces
      minTurnCount?: number; // Short-circuits to false when turnCount < minTurnCount. Defensive floor.
      conditions?: any[]; // For all_of: nested sub-conditions
    }>;
  };
  alternateSecretWin?: {
    type: 'reach_row' | 'capture_target' | 'turn_limit' | 'multiple' | 'capture_all_enemies' | 'reach_row_with_all_alive' | 'reach_row_with_both_pieces' | 'reach_row_with_both_pieces_anywhere' | 'capture_all_enemies_with_survivors';
    target?: string;
    secondTarget?: string; // second piece type for reach_row_with_both_pieces
    targetSide?: 'light' | 'dark';
    row?: number;
    turnLimit?: number;
    expectedCount?: number;
    survivorTarget?: string; // piece type that must survive
    survivorSide?: 'light' | 'dark'; // side of survivor pieces  
    minSurvivors?: number; // minimum number of survivors required
  };
  regularWin?: {
    type: 'reach_row' | 'capture_target' | 'turn_limit' | 'multiple' | 'capture_all_enemies';
    target?: string;
    targetSide?: 'light' | 'dark';
    row?: number;
    turnLimit?: number;
    minTurnCount?: number; // Top-level floor: regularWin cannot fire below this turn count.
    requireKing?: boolean; // For capture_target/reach_row: only count the king.
    conditions?: Array<{
      type: 'reach_row' | 'capture_target' | 'turn_limit' | 'capture_all_enemies_with_survivors';
      target?: string;
      targetSide?: 'light' | 'dark';
      row?: number;
      turnLimit?: number;
      survivorTarget?: string;
      survivorSide?: 'light' | 'dark';
      minSurvivors?: number;
      requireKing?: boolean; // For capture_target: only count the king's presence, not regular pieces of this type
      minTurnCount?: number; // Short-circuits to false when turnCount < minTurnCount. Defensive floor against stale-board false fires.
    }>;
  };
  // Loss conditions
  lossConditions?: Array<{
    type: 'piece_captured' | 'enemy_reaches_row' | 'turn_limit' | 'target_not_captured' | 'all_pieces_captured' | 'monkey_gamble_failure';
    target?: string; // piece type
    targetSide?: 'light' | 'dark';
    row?: number;
    turnLimit?: number;
    requireKing?: boolean; // For enemy_reaches_row + piece_captured: only fire on the king (not regular pieces of the same type).
  }>;
  descriptions: {
    regular: string;
    secret: string;
    alternateSecret?: string;
    loss?: string;
  };
}

export interface TutorialWinCondition {
  // Function that checks if the win condition is met
  // Returns 'regular' | 'secret' | 'loss' | null
  checkWin: (position: Position, piece: IPiece, turnCount: number) => 'regular' | 'secret' | 'loss' | null;
  regularDescription: string;
  secretDescription: string;
  lossDescription?: string;
}

// Reusable win condition configurations
export const TUTORIAL_WIN_CONFIGS: { [constellation: string]: { [tutorialIndex: number]: WinConditionConfig } } = {
  'Bear': {
    0: { // Bear tutorial 1
      secretWin: { type: 'reach_row', targetSide: 'light', row: 7 },
      regularWin: { type: 'turn_limit', turnLimit: 2 },
      descriptions: {
        regular: "Complete the tutorial within 2 moves",
        secret: "Reach the top row (row 7) with your bear",
        loss: "Better luck next time!"
      }
    },
    1: COMMON_RACE_PATTERNS.STANDARD_RACE('Bear'), // Bear tutorial 2 - "How to race"
    2: { // Bear tutorial 3 - "Predator" - Multiple win conditions
      secretWin: { type: 'capture_target', target: 'Goat', targetSide: 'dark', turnLimit: 6 },
      alternateSecretWin: { type: 'capture_all_enemies' },
      regularWin: { type: 'multiple', conditions: [
        { type: 'capture_target', target: 'Goat', targetSide: 'dark' },
        { type: 'reach_row', target: 'Bear', targetSide: 'light', row: 7 }
      ]},
      lossConditions: [
        { type: 'enemy_reaches_row', target: 'Goat', targetSide: 'dark', row: 0 }
      ],
      descriptions: {
        regular: "Capture the dark goat king OR reach row 7 with your bear",
        secret: "Capture the dark goat king by turn 6",
        alternateSecret: "Capture ALL enemy pieces to show your dominance!",
        loss: "Better luck next time!"
      }
    },
    3: { // Bear tutorial 4 - "Race defence" - Survive 7 turns, capture the dark crane king, or race the bear king to row 7
      // minTurnCount: 2 — no legal capture of the dark crane king or row-7 race
      // is reachable on turn 0 or 1 from the starting FEN. Defense-in-depth
      // alongside the universal SECRET-T1-BLOCKED backstop in isSecretVictoryEligible.
      secretWin: { type: 'multiple', conditions: [
        { type: 'capture_target', target: 'Crane', targetSide: 'dark', requireKing: true, minTurnCount: 2 },
        { type: 'reach_row', target: 'Bear', targetSide: 'light', row: 7, requireKing: true, minTurnCount: 2 }
      ]},
      regularWin: { type: 'turn_limit', turnLimit: 7 },
      lossConditions: [
        { type: 'piece_captured', target: 'Bear', targetSide: 'light' },
        { type: 'enemy_reaches_row', target: 'Crane', targetSide: 'dark', row: 0 }
      ],
      descriptions: {
        regular: "You stopped the enemy dark crane king from racing!",
        secret: "Congratulations! You captured the enemy crane king or raced your bear king to victory!",
        loss: "The dark crane king reached your backrank"
      }
    },
    4: { // Bear tutorial 5 - Challenge
      // Secret victory = won any of the three regular paths AND at least one
      // non-king bear is still alive (so 2+ bears total counting the king).
      // The `all_of` wraps each win path with a `pieces_alive` survivor check;
      // `multiple` ORs the wrapped paths.
      secretWin: { type: 'multiple', conditions: [
        // Race your bear king to row 7 with a non-king bear surviving.
        // minTurnCount: 2 is a defensive floor — no legal first-turn win exists.
        { type: 'all_of', minTurnCount: 2, conditions: [
          { type: 'reach_row', target: 'Bear', targetSide: 'light', row: 7, requireKing: true },
          { type: 'pieces_alive', target: 'Bear', targetSide: 'light', minCount: 1, mustNotBeKing: true }
        ]},
        // Capture the rhino king with a non-king bear surviving.
        { type: 'all_of', minTurnCount: 2, conditions: [
          { type: 'capture_target', target: 'Rhino', targetSide: 'dark', requireKing: true },
          { type: 'pieces_alive', target: 'Bear', targetSide: 'light', minCount: 1, mustNotBeKing: true }
        ]},
        // Capture every dark rhino with a non-king bear surviving
        { type: 'all_of', minTurnCount: 2, conditions: [
          { type: 'capture_target', target: 'Rhino', targetSide: 'dark' },
          { type: 'pieces_alive', target: 'Bear', targetSide: 'light', minCount: 1, mustNotBeKing: true }
        ]}
      ]},
      // minTurnCount: 2 on every regular-win predicate. From the starting FEN
      // no legal turn-0 or turn-1 win exists, and at least one path
      // (`capture_all_enemies_with_survivors`) has no other defensive floor —
      // a stale/empty snapshot was triggering it on turn 1. Mirrors the
      // secretWin defensive floors above.
      regularWin: { type: 'multiple', conditions: [
        { type: 'reach_row', target: 'Bear', targetSide: 'light', row: 7, requireKing: true, minTurnCount: 2 },
        { type: 'capture_all_enemies_with_survivors', target: 'Rhino', targetSide: 'dark', survivorTarget: 'Bear', survivorSide: 'light', minSurvivors: 1, minTurnCount: 2 },
        { type: 'capture_target', target: 'Rhino', targetSide: 'dark', requireKing: true, minTurnCount: 2 }
      ]},
      // Bear-king-capture loss handled in-config: the engine's standard
      // king-capture check is bypassed in non-solo tutorial mode (checkGameEnd
      // delegates entirely to the TutorialEngineAdapter), so we own this
      // explicitly. `requireKing: true` ensures only losing the bear KING
      // ends the game — losing a non-king bear is part of normal Bear-5 play.
      // We still own the race-loss case (rhino king at row 0) because the
      // engine's race check only fires when the dark king sits on row 0 at
      // end-of-turn; the tutorial check is more responsive within the move.
      lossConditions: [
        { type: 'piece_captured', target: 'Bear', targetSide: 'light', requireKing: true },
        { type: 'enemy_reaches_row', target: 'Rhino', targetSide: 'dark', row: 0, requireKing: true }
      ],
      descriptions: {
        regular: "Capture the dark rhino king, race your bear king to row 7, OR capture all rhinos with at least 1 bear surviving",
        secret: "Outstanding! You won with 2 or more bears left!",
        loss: "A dark rhino king reached your backrank!" // Fallback only — TutorialGame's dynamic-message logic sets a king-capture–specific string when the bear king is the one taken.
      }
    }
  },
  'Crane': {
    0: { // Crane tutorial 1 - 1v1 Crane duel: race to row 7 OR capture enemy crane king
      // minTurnCount: 2 — kings start 3 squares apart on the same rank, so no
      // capture is reachable on turns 0–1. Mirrors the universal
      // SECRET-T1-BLOCKED backstop at the data layer.
      secretWin: { type: 'capture_target', target: 'Crane', targetSide: 'dark', requireKing: true, minTurnCount: 2 },
      // requireKing + minTurnCount harden the race check against spurious
      // turn-1 fires: light crane king starts at row 1 and moves max 2 sq, so
      // row 7 takes 3+ player moves. Only the king reaching row 7 counts.
      regularWin: { type: 'reach_row', target: 'Crane', targetSide: 'light', row: 7, requireKing: true, minTurnCount: 2 },
      lossConditions: [
        { type: 'piece_captured', target: 'Crane', targetSide: 'light', requireKing: true },
        { type: 'enemy_reaches_row', target: 'Crane', targetSide: 'dark', row: 0, requireKing: true },
        { type: 'turn_limit', turnLimit: 8 }
      ],
      descriptions: {
        regular: 'Race your crane to row 7',
        secret: 'Capture the dark crane king for a swift victory!',
        loss: 'Better luck next time!'
      }
    },
    1: createRaceToTop({
      // Bird Type: crane king starts at row 0, so dual-direction would let the
      // player win immediately with any sideways move. Race must be to row 7
      // (working around the dark bears that crane cannot capture).
      pieceType: 'Crane',
      secretWinTurnLimit: 5, // Secret victory if you reach row 7 by turn 5
      overallTurnLimit: 8, // Loss if not completed by turn 8
      descriptions: {
        regular: 'Race your crane to row 7 (around the bears!)',
        secret: 'Race your crane to row 7 by turn 5!',
        loss: 'Better luck next time!'
      }
    }), // Crane tutorial 2 - "Bird Type": race to row 7, secret by turn 5
    2: createRaceToTop({ pieceType: 'Crane', secretWinTurnLimit: 5, overallTurnLimit: 7 }) // Crane tutorial 3 - Race to row 7, secret win by turn 5
  },
  'Tree': {
    0: { // Tree tutorial 1 - Tree movement
      secretWin: { type: 'reach_row', target: 'Tree', targetSide: 'light', row: 7, turnLimit: 5 },
      regularWin: { type: 'multiple', conditions: [
        { type: 'reach_row', target: 'Tree', targetSide: 'light', row: 7, turnLimit: 6 },
        { type: 'capture_target', target: 'Bear', targetSide: 'dark' }
      ]},
      lossConditions: [
        { type: 'piece_captured', target: 'Tree', targetSide: 'light' },
        { type: 'turn_limit', turnLimit: 6 }
      ],
      descriptions: {
        regular: "Race your tree to row 7 by turn 6 OR capture the bear!",
        secret: "Amazing! You reached row 7 by turn 5!",
        loss: "Better luck next time!"
      }
    },
    1: { // Tree tutorial 2
      secretWin: { type: 'reach_row_with_both_pieces_anywhere', target: 'Crane', secondTarget: 'Tree', targetSide: 'light', row: 7 },
      regularWin: { type: 'reach_row', target: 'Crane', targetSide: 'light', row: 7 },
      lossConditions: [
        { type: 'piece_captured', target: 'Crane', targetSide: 'light' }
      ],
      descriptions: {
        regular: "Get your crane king to the top row (row 7)",
        secret: "Get your crane to row 7 while keeping your tree alive!",
        loss: "Tutorial failed! Your crane king was captured!"
      }
    },
    2: { // Tree tutorial 3 - Tree Challenge
      regularWin: { type: 'reach_row', target: 'Tree', targetSide: 'light', row: 7 },
      secretWin: { type: 'reach_row_with_both_pieces_anywhere', target: 'Tree', secondTarget: 'Bear', targetSide: 'light', row: 7 },
      lossConditions: [
        { type: 'piece_captured', target: 'Tree', targetSide: 'light' }
      ],
      descriptions: {
        regular: "You did it! Tree reached row 7!",
        secret: "Amazing! Both Tree and Bear reached row 7!",
        loss: "Your Tree King was captured!"
      }
    }
  },
  'Monkey': {
    0: { // Monkey tutorial 1 - Movement basics (Solo)
      regularWin: { type: 'reach_row', target: 'Monkey', targetSide: 'light', row: 7, turnLimit: 3 },
      secretWin: { type: 'reach_row', target: 'Monkey', targetSide: 'light', row: 7, turnLimit: 2 },
      lossConditions: [
        { type: 'turn_limit', turnLimit: 3 }
      ],
      descriptions: {
        regular: "Reach row 7 within 3 turns!",
        secret: "Amazing! You reached row 7 in just 2 moves!",
        loss: "You didn't reach row 7 in time!"
      }
    },
    1: { // Monkey tutorial 2 - "Swing ability"
      regularWin: { type: 'reach_row', target: 'Monkey', targetSide: 'light', row: 7 },
      secretWin: { type: 'reach_row', target: 'Monkey', targetSide: 'light', row: 7 }, // Same as regular for now
      lossConditions: [
        { type: 'piece_captured', target: 'Monkey', targetSide: 'light' }
      ],
      descriptions: {
        regular: "Race your Monkey-King to row 7 to win!",
        secret: "Race your Monkey-King to row 7 to win!",
        loss: "Your Monkey-King was captured!"
      }
    },
    2: { // Monkey tutorial 3 - "Gamble" with dice roll mechanics  
      regularWin: { type: 'reach_row', target: 'Monkey', targetSide: 'light', row: 7 },
      secretWin: { type: 'capture_target', target: 'Bear', targetSide: 'dark' },
      lossConditions: [
        { type: 'turn_limit', turnLimit: 13 },
        { type: 'piece_captured', target: 'Monkey', targetSide: 'light' },
        { type: 'monkey_gamble_failure' } // Special dice roll failure detection
      ],
      descriptions: {
        regular: "You did it!",
        secret: "You did it!",
        loss: "Better luck next time!"
      }
    },
    3: { // Monkey tutorial 4 - "Consume Ability"
      regularWin: { type: 'reach_row', target: 'Fungi', targetSide: 'light', row: 7 },
      secretWin: { type: 'reach_row', target: 'Monkey', targetSide: 'light', row: 7 }, // Same as regular for now
      descriptions: {
        regular: "Race your Fungi-King to row 7 to win!",
        secret: "You did it!",
        loss: "Better luck next time!"
      }
    },
    4: { // Monkey tutorial 5 - "Monkey Challenge"
      regularWin: { type: 'reach_row', target: 'Monkey', targetSide: 'light', row: 7 },
      secretWin: { type: 'capture_target', target: 'Crane', targetSide: 'dark' },
      lossConditions: [
        { type: 'enemy_reaches_row', target: 'Crane', targetSide: 'dark', row: 0 },
        { type: 'piece_captured', target: 'Monkey', targetSide: 'light' }
      ],
      descriptions: {
        regular: "Race your Monkey-King to row 7 to win!",
        secret: "Capture the dark crane for a secret victory!",
        loss: "Tutorial failed! The dark crane outmaneuvered your monkey!"
      }
    }
  },
  'Wolf': {
    0: { // Wolf tutorial 1 - "Wolf movement" 
      regularWin: { type: 'reach_row', target: 'Wolf', targetSide: 'light', row: 7 },
      secretWin: { type: 'reach_row', target: 'Wolf', targetSide: 'light', row: 7 }, // Same as regular for now
      lossConditions: [
        { type: 'enemy_reaches_row', target: 'Bear', targetSide: 'dark', row: 0 },
        { type: 'piece_captured', target: 'Wolf', targetSide: 'light' }
      ],
      descriptions: {
        regular: "Race your Wolf-King to row 7 to win!",
        secret: "Race your Wolf-King to row 7 to win!",
        loss: "Tutorial failed! The dark bear outmaneuvered your wolf!"
      }
    },
    1: { // Wolf tutorial 2 - "Going it alone"
      regularWin: { type: 'reach_row', target: 'Wolf', targetSide: 'light', row: 7 },
      secretWin: { type: 'reach_row_with_both_pieces_anywhere', target: 'Wolf', secondTarget: 'Tree', targetSide: 'light', row: 7 },
      lossConditions: [
        { type: 'piece_captured', target: 'Wolf', targetSide: 'light' }
      ],
      descriptions: {
        regular: "Win with just your lone Wolf remaining!",
        secret: "Win while keeping both your Wolf and Tree alive!",
        loss: "Your lone wolf was captured! The pack needs its leader!"
      }
    },
    2: { // Wolf tutorial 3 - "Wolf Challenge"
      regularWin: { type: 'reach_row', target: 'Wolf', targetSide: 'light', row: 7 },
      secretWin: { type: 'reach_row', target: 'Wolf', targetSide: 'light', row: 7 }, // Same as regular for now
      lossConditions: [
        { type: 'piece_captured', target: 'Wolf', targetSide: 'light' }
      ],
      descriptions: {
        regular: "Race your Wolf-King to row 7 to win!",
        secret: "Race your Wolf-King to row 7 to win!",
        loss: "Your Wolf-King was captured! The pack has lost its leader!"
      }
    }
  },
  'Rhino': {
    0: { // Rhino tutorial 1 - "Rhino movement"
      regularWin: { type: 'reach_row', target: 'Rhino', targetSide: 'light', row: 7 },
      secretWin: { type: 'reach_row', target: 'Rhino', targetSide: 'light', row: 7 }, // Same as regular for now
      lossConditions: [
        { type: 'piece_captured', target: 'Rhino', targetSide: 'light' },
        { type: 'enemy_reaches_row', target: 'Wolf', targetSide: 'dark', row: 0 }
      ],
      descriptions: {
        regular: "Race your Rhino-King to row 7 to win!",
        secret: "Race your Rhino-King to row 7 to win!",
        loss: "Better luck next time!"
      }
    }
  }
};

// Generic win condition checking functions
function checkWinCondition(
  config: WinConditionConfig,
  position: Position,
  piece: IPiece,
  turnCount: number,
  board: any[][]
): 'regular' | 'secret' | 'loss' | null {

  // CRITICAL FIX: Check LOSS conditions FIRST to handle immediate failures
  // Then check win conditions with proper priority order

  // Top-level minTurnCount floor: a secretWin config can declare a minimum
  // turn count below which it cannot fire, even if `checkWinConditionType`
  // would otherwise say true. Mirrors the nested-condition floor at line ~439.
  const secretFloorBlocks = (cfg: WinConditionConfig['secretWin'] | WinConditionConfig['alternateSecretWin']): boolean => {
    return !!(cfg && (cfg as any).minTurnCount && turnCount < (cfg as any).minTurnCount);
  };

  // Check for loss conditions first - these should always take precedence unless explicitly overridden
  if (config.lossConditions) {
    for (const lossCondition of config.lossConditions) {
      if (checkLossCondition(lossCondition, turnCount, board)) {
        // Before returning loss, check if we've achieved a win condition that should override the loss
        const secretWin = !secretFloorBlocks(config.secretWin) && config.secretWin && checkWinConditionType(config.secretWin, position, piece, turnCount, board);
        const regularWin = config.regularWin && checkWinConditionType(config.regularWin, position, piece, turnCount, board);

        // Only override loss if we've genuinely achieved the tutorial goal
        if (secretWin) {
          return 'secret';
        }
        if (regularWin) {
          return 'regular';
        }

        return 'loss';
      }
    }
  }

  // Check secret win condition first (more challenging, should take precedence)
  if (config.secretWin && !secretFloorBlocks(config.secretWin) && checkWinConditionType(config.secretWin, position, piece, turnCount, board)) {
    return 'secret';
  }

  // Check alternate secret win condition (if available)
  if (config.alternateSecretWin && !secretFloorBlocks(config.alternateSecretWin) && checkWinConditionType(config.alternateSecretWin, position, piece, turnCount, board)) {
    return 'secret';
  }

  // Check regular win condition last (easier condition)
  if (config.regularWin && checkWinConditionType(config.regularWin, position, piece, turnCount, board)) {
    return 'regular';
  }

  return null;
}

// Universal turn-1 secret-victory backstop. No Zodiac tutorial has a legal
// secret win on turn 0 or 1 — a single piece move cannot capture a king or
// race to row 7 from any starting FEN. Both the UI win-check and the engine
// adapter call into this so the threshold lives in one place.
//
// Source of truth for the threshold used by both backstops; if you change
// the floor, update this constant and the docs in
// memory/alchemist-mcts-state-isolation.md.
export const SECRET_VICTORY_MIN_TURN = 2;

export function isSecretVictoryEligible(
  winType: 'regular' | 'secret' | 'loss' | null,
  turnCount: number,
  source: 'ui' | 'engine',
  diagContext: string
): boolean {
  if (winType !== 'secret') return true;
  if (turnCount >= SECRET_VICTORY_MIN_TURN) return true;
  // eslint-disable-next-line no-console
  console.warn(`[TUT-DIAG] SECRET-T1-BLOCKED src=${source} ${diagContext} turn=${turnCount}`);
  return false;
}

function checkWinConditionType(
  condition: any,
  position: Position,
  piece: IPiece,
  turnCount: number,
  board: any[][]
): boolean {
  // Defensive floor: any condition can declare a minimum turn count below
  // which it cannot fire. Guards against spurious turn-1 secret victories
  // until we identify the root cause (see plan: bear-5 secret victory bug).
  if (condition.minTurnCount && turnCount < condition.minTurnCount) {
    bear5Log(`MIN-TURN-BLOCK type=${condition.type} turnCount=${turnCount} minTurnCount=${condition.minTurnCount}`);
    bear4Log(`MIN-TURN-BLOCK type=${condition.type} turnCount=${turnCount} minTurnCount=${condition.minTurnCount}`);
    return false;
  }

  switch (condition.type) {
    case 'reach_row':

      if (condition.turnLimit && turnCount > condition.turnLimit) {
        return false;
      }
      
      // FIXED: Scan the entire board for the target piece instead of relying on passed piece
      // This prevents issues when the engine passes the wrong piece
      if (condition.target) {
        const targetRow = condition.row;
        let foundTargetPiece = false;

        // Scan the target row for the specific piece type. `requireKing`
        // mirrors real Zodiac rules: only kings score race victories. Regular
        // pieces stepping on the rank shouldn't count.
        for (let col = 0; col < board[targetRow]?.length || 0; col++) {
          const square = board[targetRow][col];
          if (!square?.Pieces || square.Pieces.length === 0) continue;

          if (square.Pieces.some((p: any) =>
            p.type === condition.target &&
            p.isLight === (condition.targetSide === 'light') &&
            (!condition.requireKing || p.isKing === true)
          )) {
            foundTargetPiece = true;
            break;
          }
        }

        bear5Log(`SUBCOND type=reach_row target=${condition.target} side=${condition.targetSide} row=${targetRow} requireKing=${!!condition.requireKing} result=${foundTargetPiece}`);
        bear4Log(`SUBCOND type=reach_row target=${condition.target} side=${condition.targetSide} row=${targetRow} requireKing=${!!condition.requireKing} result=${foundTargetPiece}`);
        return foundTargetPiece;
      } else {
        // Fallback to original logic if no specific target piece
        if (condition.targetSide === 'light' && !piece.isLight) {
          return false;
        }
        if (condition.targetSide === 'dark' && piece.isLight) {
          return false;
        }
        
        const success = position.row === condition.row;
        return success;
      }
      
    case 'capture_target':

      if (condition.turnLimit && turnCount > condition.turnLimit) {
        return false;
      }

      // Check if target piece type is no longer on the board. When
      // `requireKing` is set, only the king piece's presence blocks the win —
      // matches Zodiac's "capture the king to win" rule even when regular
      // pieces of the same type are still around.
      let targetExists = false;
      let foundPieces: string[] = [];

      board.forEach((row, rowIndex) => {
        row.forEach((square, colIndex) => {
          square.Pieces?.forEach((p: any) => {
            if (p.type === condition.target) {
              foundPieces.push(`${p.type}${p.isKing ? 'K' : ''} ${p.isLight ? 'Light' : 'Dark'} at (${rowIndex},${colIndex})`);
              if (p.isLight === (condition.targetSide === 'light') &&
                  (!condition.requireKing || p.isKing === true)) {
                targetExists = true;
              }
            }
          });
        });
      });

      bear5Log(`CAPTURE-TARGET target=${condition.target} side=${condition.targetSide} requireKing=${!!condition.requireKing} foundPieces=[${foundPieces.join('; ')}] targetExists=${targetExists} result=${!targetExists}`);
      bear4Log(`CAPTURE-TARGET target=${condition.target} side=${condition.targetSide} requireKing=${!!condition.requireKing} foundPieces=[${foundPieces.join('; ')}] targetExists=${targetExists} result=${!targetExists}`);

      // Stale-board sentinel: `capture_target` reads "target captured" as
      // `!targetExists`, so an empty board snapshot would always pass even
      // though no capture has happened. Block only on a literally empty
      // board (AI search clone gap, pre-FEN-load context lag) — a single
      // surviving piece IS a legitimate end state for 1v1 king-duel
      // tutorials (e.g. Crane[0]), where capturing the enemy king leaves
      // exactly one piece on the board.
      if (!targetExists) {
        let totalPieces = 0;
        for (const r of board) {
          for (const sq of r) {
            totalPieces += sq.Pieces?.length || 0;
          }
        }
        if (totalPieces < 1) {
          // eslint-disable-next-line no-console
          console.warn(`[TUT-DIAG] CAPTURE-TARGET-STALE-BOARD target=${condition.target} side=${condition.targetSide} totalPieces=${totalPieces} turn=${turnCount}`);
          return false;
        }
      }

      return !targetExists; // Win if target is captured (doesn't exist)
      
    case 'turn_limit':
      return turnCount >= condition.turnLimit;
      
    case 'multiple': {
      // Check if ANY of the conditions is met (OR logic). When bear5Diag is
      // active, log which child path returned true so we can identify the
      // misfiring path in production logs.
      const subs = condition.conditions || [];
      for (let i = 0; i < subs.length; i++) {
        if (checkWinConditionType(subs[i], position, piece, turnCount, board)) {
          bear5Log(`PATH-FIRED pathIndex=${i} pathType=${subs[i]?.type}`);
          bear4Log(`PATH-FIRED pathIndex=${i} pathType=${subs[i]?.type}`);
          return true;
        }
      }
      return false;
    }

    case 'all_of':
      // Check if ALL sub-conditions are met (AND logic). Pairs with `multiple`
      // when you need composed predicates — e.g. "won this way AND a
      // non-king bear is still alive".
      return condition.conditions?.every((subCondition: any) =>
        checkWinConditionType(subCondition, position, piece, turnCount, board)
      ) || false;

    case 'pieces_alive': {
      // Count pieces matching target/side, optionally filtered to kings or
      // non-kings. Useful as a survivor predicate in AND-composed secrets.
      const aliveCount = board.flatMap(row =>
        row.flatMap(square =>
          square.Pieces?.filter((p: any) =>
            p.type === condition.target &&
            p.isLight === (condition.targetSide === 'light') &&
            (!condition.mustBeKing || p.isKing === true) &&
            (!condition.mustNotBeKing || p.isKing !== true)
          ) || []
        )
      ).length;
      const result = aliveCount >= (condition.minCount || 1);
      bear5Log(`SUBCOND type=pieces_alive target=${condition.target} side=${condition.targetSide} mustBeKing=${!!condition.mustBeKing} mustNotBeKing=${!!condition.mustNotBeKing} aliveCount=${aliveCount} minCount=${condition.minCount || 1} result=${result}`);
      bear4Log(`SUBCOND type=pieces_alive target=${condition.target} side=${condition.targetSide} mustBeKing=${!!condition.mustBeKing} mustNotBeKing=${!!condition.mustNotBeKing} aliveCount=${aliveCount} minCount=${condition.minCount || 1} result=${result}`);
      return result;
    }
      
    case 'capture_all_enemies':
      
      // Check turn limit first if specified
      if (condition.turnLimit && turnCount > condition.turnLimit) {
        return false;
      }
      
      // Check if there are NO enemy pieces remaining on the board
      // For tutorials, "enemies" are always the dark pieces (player controls light pieces)
      const darkPiecesExist = board.some(row => 
        row.some(square => 
          square.Pieces?.some((p: any) => 
            !p.isLight // Dark pieces are the enemies in tutorials
          )
        )
      );
      
      const allEnemiesCaptured = !darkPiecesExist;
      return allEnemiesCaptured; // Win if no dark pieces exist
      
    case 'capture_all_enemies_with_survivors':
      // Check if all enemies (specified target type) are captured AND minimum survivors remain

      // First check if all enemy pieces of the target type are captured
      const enemyPiecesOfTargetType = board.flatMap(row =>
        row.flatMap(square =>
          square.Pieces?.filter((p: any) =>
            p.type === condition.target &&
            p.isLight === (condition.targetSide === 'light')
          ) || []
        )
      );

      const allTargetEnemiesCaptured = enemyPiecesOfTargetType.length === 0;

      if (!allTargetEnemiesCaptured) {
        return false; // Must capture all enemies first
      }

      // Stale-board sentinel: same pattern as `capture_target`. "All enemies
      // captured" reads as `length === 0`, which is trivially true for an
      // empty/stale board snapshot (AI search clone gap, pre-FEN-load
      // context lag). Bear-5 was hitting this on turn 1 — diagnostic logs
      // showed `PATH-FIRED pathType=capture_all_enemies_with_survivors`
      // when 5 dark Rhinos were still alive. Block on a literally empty
      // board; anything with at least one piece is a real game state.
      let totalPiecesAES = 0;
      for (const r of board) {
        for (const sq of r) {
          totalPiecesAES += sq.Pieces?.length || 0;
        }
      }
      if (totalPiecesAES < 1) {
        // eslint-disable-next-line no-console
        console.warn(`[TUT-DIAG] CAPTURE-ALL-ENEMIES-STALE-BOARD target=${condition.target} side=${condition.targetSide} totalPieces=${totalPiecesAES} turn=${turnCount}`);
        return false;
      }

      // Then check if minimum number of survivor pieces remain
      const survivorPieces = board.flatMap(row =>
        row.flatMap(square =>
          square.Pieces?.filter((p: any) =>
            p.type === condition.survivorTarget &&
            p.isLight === (condition.survivorSide === 'light')
          ) || []
        )
      );

      const hasSufficientSurvivors = survivorPieces.length >= (condition.minSurvivors || 1);

      return allTargetEnemiesCaptured && hasSufficientSurvivors;
      
    case 'reach_row_with_all_alive':
      // Check if piece reaches target row AND all pieces of same type are still alive
      if (condition.turnLimit && turnCount > condition.turnLimit) return false;
      if (condition.target && piece.type !== condition.target) return false;
      if (condition.targetSide === 'light' && !piece.isLight) return false;
      if (condition.targetSide === 'dark' && piece.isLight) return false;
      
      // First check if piece reached target row
      const reachedRow = position.row === condition.row;
      if (!reachedRow) return false;
      
      // Then check if ALL pieces of the same type are still alive
      const allPiecesOfType = board.flatMap(row => 
        row.flatMap(square => 
          square.Pieces?.filter((p: any) => 
            p.type === condition.target && 
            p.isLight === (condition.targetSide === 'light')
          ) || []
        )
      );
      
      const expectedCount = condition.expectedCount || 2; // Default to 2 for Bear Tutorial 5
      const allAlive = allPiecesOfType.length >= expectedCount;
      
      
      return reachedRow && allAlive;
      
    case 'reach_row_with_both_pieces':
      // Check if both pieces (target and secondTarget) are on the same square at the target row
      if (condition.turnLimit && turnCount > condition.turnLimit) return false;
      if (condition.targetSide === 'light' && !piece.isLight) return false;
      if (condition.targetSide === 'dark' && piece.isLight) return false;
      
      // Don't just check the moving piece position - scan the entire target row for both pieces on same square
      const targetRow = condition.row;
      
      // Scan all squares in the target row to find if both pieces are stacked together
      let foundBothPiecesOnSameSquare = false;
      
      for (let col = 0; col < board[targetRow]?.length || 0; col++) {
        const square = board[targetRow][col];
        if (!square?.Pieces || square.Pieces.length === 0) continue;
        
        const hasPrimaryPiece = square.Pieces.some((p: any) => 
          p.type === condition.target && 
          p.isLight === (condition.targetSide === 'light')
        );
        
        const hasSecondaryPiece = square.Pieces.some((p: any) => 
          p.type === condition.secondTarget && 
          p.isLight === (condition.targetSide === 'light')
        );
        
        if (hasPrimaryPiece && hasSecondaryPiece) {
          foundBothPiecesOnSameSquare = true;
          break;
        }
      }
      
      
      return foundBothPiecesOnSameSquare;

    case 'reach_row_with_both_pieces_anywhere':
      // Check if primary piece reached target row AND secondary piece is still alive anywhere on board
      if (condition.turnLimit && turnCount > condition.turnLimit) return false;
      const targetRowAnywhere = condition.row;
      
      let foundPrimaryPiece = false;
      let foundSecondaryPieceAlive = false;
      
      // First, check if primary piece reached the target row
      for (let col = 0; col < board[targetRowAnywhere]?.length || 0; col++) {
        const square = board[targetRowAnywhere][col];
        if (!square?.Pieces || square.Pieces.length === 0) continue;
        
        if (square.Pieces.some((p: any) => 
          p.type === condition.target && 
          p.isLight === (condition.targetSide === 'light')
        )) {
          foundPrimaryPiece = true;
          break;
        }
      }
      
      // Second, check if secondary piece is alive anywhere on the board
      for (let row = 0; row < board.length; row++) {
        for (let col = 0; col < board[row].length; col++) {
          const square = board[row][col];
          if (!square?.Pieces || square.Pieces.length === 0) continue;
          
          if (square.Pieces.some((p: any) => 
            p.type === condition.secondTarget && 
            p.isLight === (condition.targetSide === 'light')
          )) {
            foundSecondaryPieceAlive = true;
            break;
          }
        }
        if (foundSecondaryPieceAlive) break;
      }
      
      const primaryReachedAndSecondaryAlive = foundPrimaryPiece && foundSecondaryPieceAlive;
      
      return primaryReachedAndSecondaryAlive;
      
    default:
      return false;
  }
}

function checkLossCondition(condition: any, turnCount: number, board: any[][]): boolean {
  switch (condition.type) {
    case 'piece_captured':
      // Check if the specified piece type is no longer on the board. When
      // `requireKing` is set, only the king's absence triggers — non-king
      // pieces of the same type being captured doesn't end the game. This
      // matches Bear 5's design: losing a non-king bear is part of normal
      // play, only losing the bear KING ends the tutorial as a loss.
      const pieceExists = board.some(row =>
        row.some(square =>
          square.Pieces?.some((p: any) =>
            p.type === condition.target &&
            p.isLight === (condition.targetSide === 'light') &&
            (!condition.requireKing || p.isKing === true)
          )
        )
      );
      return !pieceExists; // Loss if piece was captured
      
    case 'all_pieces_captured':
      // Check if ALL pieces of the specified type and side are captured (no longer on board)
      const anyPieceExists = board.some(row => 
        row.some(square => 
          square.Pieces?.some((p: any) => 
            p.type === condition.target && 
            p.isLight === (condition.targetSide === 'light')
          )
        )
      );
      
      
      // For debugging: count actual pieces
      const allPiecesOfType = board.flatMap(row => 
        row.flatMap(square => 
          square.Pieces?.filter((p: any) => 
            p.type === condition.target && 
            p.isLight === (condition.targetSide === 'light')
          ) || []
        )
      );
      
      return !anyPieceExists; // Loss if NO pieces of this type exist (all captured)
      
    case 'enemy_reaches_row':
      // Check if enemy piece reached the specified row. When `requireKing` is
      // set, only fire if a *king* piece of the given type/side is on the row
      // (real Zodiac rules: only kings win the race; regular pieces touching
      // the backrank shouldn't end the game).
      return board[condition.row]?.some(square =>
        square.Pieces?.some((p: any) =>
          p.type === condition.target &&
          p.isLight === (condition.targetSide === 'light') &&
          (!condition.requireKing || p.isKing === true)
        )
      ) || false;
      
    case 'turn_limit':
      return turnCount >= condition.turnLimit;
      
    case 'target_not_captured':
      if (turnCount < condition.turnLimit) return false;
      // Check if target still exists after turn limit
      const targetStillExists = board.some(row => 
        row.some(square => 
          square.Pieces?.some((p: any) => 
            p.type === condition.target && 
            p.isLight === (condition.targetSide === 'light')
          )
        )
      );
      return targetStillExists; // Loss if target still exists after turn limit
      
    case 'monkey_gamble_failure':
      // Special case for Monkey gamble dice roll failures
      // Check if there's a monkey_gamble_failure event in the game state
      const gameState = (board as any).gameState;
      if (gameState?.lastMonkeyGambleFailure) {
        const failure = gameState.lastMonkeyGambleFailure;
        // Position is already set by GameStateManager.setMonkeyGambleFailure()
        return true;
      }
      return false;
      
    default:
      return false;
  }
}

// Convert configs to the old format for compatibility
function createTutorialWinCondition(config: WinConditionConfig): TutorialWinCondition {
  return {
    checkWin: (position: Position, piece: IPiece, turnCount: number) => 
      checkWinCondition(config, position, piece, turnCount, []), // board will be passed separately
    regularDescription: config.descriptions.regular,
    secretDescription: config.descriptions.secret,
    lossDescription: config.descriptions.loss
  };
}

// Tutorial win conditions mapping
export const TUTORIAL_WIN_CONDITIONS: { [constellation: string]: { [tutorialIndex: number]: TutorialWinCondition } } = {};

// Initialize the mapping from configs
Object.keys(TUTORIAL_WIN_CONFIGS).forEach(constellation => {
  TUTORIAL_WIN_CONDITIONS[constellation] = {};
  Object.keys(TUTORIAL_WIN_CONFIGS[constellation]).forEach(tutorialIndex => {
    const index = parseInt(tutorialIndex);
    TUTORIAL_WIN_CONDITIONS[constellation][index] = createTutorialWinCondition(
      TUTORIAL_WIN_CONFIGS[constellation][index]
    );
  });
});

/**
 * Check if a tutorial win condition is met
 * @param constellation The constellation name
 * @param tutorialIndex The tutorial index (0-based)
 * @param position The current position of the piece
 * @param piece The piece that moved
 * @param turnCount The current turn count
 * @param board The game board for checking captures and positions
 * @returns 'regular' | 'secret' | null
 */
export function checkTutorialWinCondition(
  constellation: string, 
  tutorialIndex: number, 
  position: Position, 
  piece: IPiece, 
  turnCount: number,
  board?: any[][]
): 'regular' | 'secret' | 'loss' | null {
  // CRITICAL FIX: Check if there's a tutorialConfig override in the board context
  const gameStateWithConfig = board && (board as any).gameState;
  const tutorialConfig = gameStateWithConfig?.tutorialConfig;

  let actualConstellation = constellation;
  let actualTutorialIndex = tutorialIndex;

  // Use realConstellation/realTutorialIndex if available (these are the UI/player-facing tutorial indices)
  // The non-"real" indices are used for engine king-protection and should NOT be used for win conditions
  if (tutorialConfig?.realConstellation && tutorialConfig?.realTutorialIndex !== undefined) {
    actualConstellation = tutorialConfig.realConstellation;
    actualTutorialIndex = tutorialConfig.realTutorialIndex;
  } else if (tutorialConfig?.constellation && tutorialConfig?.tutorialIndex !== undefined) {
    actualConstellation = tutorialConfig.constellation;
    actualTutorialIndex = tutorialConfig.tutorialIndex;
  }
  
  
  const config = TUTORIAL_WIN_CONFIGS[actualConstellation]?.[actualTutorialIndex];
  if (!config) {
    return null; // No win condition defined for this tutorial
  }

  // Gate Bear-5 diagnostics to only fire while evaluating Bear/4.
  const wasBear5DiagActive = bear5DiagActive;
  if (actualConstellation === 'Bear' && actualTutorialIndex === 4) {
    bear5DiagActive = true;
  }
  // Gate Bear-4 diagnostics to only fire while evaluating Bear/3.
  const wasBear4DiagActive = bear4DiagActive;
  if (actualConstellation === 'Bear' && actualTutorialIndex === 3) {
    bear4DiagActive = true;
  }
  try {
    // Use the new generic checking system
    const result = checkWinCondition(config, position, piece, turnCount, board || []);
    return result;
  } finally {
    bear5DiagActive = wasBear5DiagActive;
    bear4DiagActive = wasBear4DiagActive;
  }
}

/**
 * Check if tutorial has failed based on turn limit and goal achievement
 * @param constellation The constellation name
 * @param tutorialIndex The tutorial index (0-based)
 * @param turnCount Current turn count
 * @param board Game board to check for goal achievement
 * @returns true if tutorial has failed, false otherwise
 */
export function checkTutorialFailure(
  constellation: string,
  tutorialIndex: number,
  turnCount: number,
  board: any[][]
): boolean {
  
  const config = TUTORIAL_WIN_CONFIGS[constellation]?.[tutorialIndex];
  if (!config?.lossConditions) {
    return false; // No loss conditions defined
  }
  
  
  // Check all loss conditions for this tutorial
  for (const lossCondition of config.lossConditions) {
    const result = checkLossCondition(lossCondition, turnCount, board);
    if (result) {
      return true;
    }
  }
  
  return false;
}

/**
 * Get win condition descriptions for a tutorial
 * @param constellation The constellation name
 * @param tutorialIndex The tutorial index (0-based)
 * @returns Object with regular, secret, and loss descriptions, or null
 */
export function getTutorialWinDescriptions(
  constellation: string, 
  tutorialIndex: number
): { regular: string; secret: string; loss?: string } | null {
  const winCondition = TUTORIAL_WIN_CONDITIONS[constellation]?.[tutorialIndex];
  const config = TUTORIAL_WIN_CONFIGS[constellation]?.[tutorialIndex];
  if (!winCondition) {
    return null;
  }
  
  return {
    regular: winCondition.regularDescription,
    secret: winCondition.secretDescription,
    loss: config?.descriptions?.loss
  };
}